import React, { useState, useEffect, useRef } from 'react';
import { X, Reply, Send, Paperclip, Smile, Image as ImageIcon, FileText, Loader2, Download, FileType } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { cn, getInitials } from '../lib/utils';
import { messageAPI } from '../lib/api';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import FileUploader from './discussion/FileUploader';
import MentionInput from './discussion/MentionInput';
import { extractMentionUserIds } from '../lib/mentions';

export default function ThreadPanel({ parentMessage, channelId, currentUser, isOpen, onClose, members = [] }) {
    const [replies, setReplies] = useState([]);
    const [newReply, setNewReply] = useState('');
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const repliesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && parentMessage) {
            loadReplies();
            inputRef.current?.focus();
        }
    }, [isOpen, parentMessage]);

    const loadReplies = async () => {
        if (!parentMessage) return;
        setLoading(true);
        try {
            // Get all replies for this parent message
            const { data } = await messageAPI.getByChannel(channelId, { parent_id: parentMessage.id });
            setReplies(data.messages || []);
            setTimeout(scrollToBottom, 100);
        } catch (error) {
            console.error('Failed to load replies:', error);
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendReply = async () => {
        if (!newReply.trim() && attachments.length === 0) return;

        try {
            const mappedAttachments = attachments.map((file) => ({
                id: file.id,
                filename: file.filename,
                file_url: file.file_url,
                file_type: file.content_type || file.file_type,
                file_size: file.file_size || file.size || 0,
                thumbnail_url: null,
            }));

            const response = await messageAPI.create(channelId, {
                content: newReply || '',
                message_type: 'text',
                attachments: mappedAttachments,
                mentions: extractMentionUserIds(newReply || '', members || []),
                parent_id: parentMessage.id, // Key: set parent ID
            });

            if (response.data) {
                setReplies(prev => [...prev, response.data]);
                setTimeout(scrollToBottom, 100);
            }

            setNewReply('');
            setAttachments([]);
            setShowEmojiPicker(false);
        } catch (error) {
            console.error('Failed to send reply:', error);
            toast.error('Failed to send reply');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendReply();
        }
    };

    const handleFileUpload = (uploadedFiles) => {
        setAttachments(prev => [...prev, ...uploadedFiles]);
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleEmojiClick = (emojiData) => {
        setNewReply(prev => prev + emojiData.emoji);
        setShowEmojiPicker(false);
    };

    if (!isOpen || !parentMessage) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-full sm:w-[450px] bg-white shadow-2xl z-50 flex flex-col border-l">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                    <Reply className="w-5 h-5 text-gray-600" />
                    <div>
                        <h3 className="font-semibold text-sm">Thread</h3>
                        <p className="text-xs text-gray-500">{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</p>
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="w-5 h-5" />
                </Button>
            </div>

            {/* Thread Messages */}
            <ScrollArea className="flex-1 p-4">
                {/* Parent Message */}
                <div className="mb-4 pb-4 border-b">
                    <ThreadMessage message={parentMessage} currentUser={currentUser} isParent />
                </div>

                {/* Replies */}
                {loading ? (
                    <div className="flex justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                ) : replies.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                        <Reply className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>No replies yet</p>
                        <p className="text-xs mt-1">Be the first to reply!</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {replies.map((reply) => (
                            <ThreadMessage key={reply.id} message={reply} currentUser={currentUser} />
                        ))}
                        <div ref={repliesEndRef} />
                    </div>
                )}
            </ScrollArea>

            {/* Attachments Preview - ABOVE input */}
            {attachments.length > 0 && (
                <div className="px-4 py-3 border-t bg-gray-50">
                    <div className="flex flex-wrap gap-2">
                        {attachments.map((file, index) => (
                            <AttachmentChip key={index} file={file} onRemove={() => removeAttachment(index)} />
                        ))}
                    </div>
                </div>
            )}

            {/* Reply Input */}
            <div className="p-4 border-t bg-white">
                <div className="flex items-end gap-2">
                    <div className="flex-1 relative">
                        <MentionInput
                            ref={inputRef}
                            value={newReply}
                            onChange={(e) => setNewReply(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Balas di thread ini... (ketik @ untuk mention)"
                            className="pr-20 min-h-[44px] max-h-[120px]"
                            members={members}
                            rows={1}
                        />
                        <div className="absolute right-2 top-2 flex items-center gap-1">
                            <FileUploader onFileUploaded={handleFileUpload} channelId={channelId}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Paperclip className="w-4 h-4" />
                                </Button>
                            </FileUploader>
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            >
                                <Smile className="w-4 h-4" />
                            </Button>
                        </div>
                        {showEmojiPicker && (
                            <div className="absolute bottom-12 right-0 z-10">
                                <EmojiPicker onEmojiClick={handleEmojiClick} />
                            </div>
                        )}
                    </div>
                    <Button 
                        onClick={handleSendReply} 
                        disabled={!newReply.trim() && attachments.length === 0}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        data-testid="thread-send-btn"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Thread Message Component
function ThreadMessage({ message, currentUser, isParent = false }) {
    return (
        <div className={cn("group", isParent && "bg-blue-50 p-3 rounded-lg")}>
            <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {message.user?.avatar ? (
                        <img 
                            src={message.user.avatar} 
                            alt={message.user.name}
                            className="w-8 h-8 rounded-full"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-medium">
                            {getInitials(message.user?.name || 'U')}
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{message.user?.name || 'Unknown'}</span>
                        <span className="text-xs text-gray-400">
                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isParent && (
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                Original
                            </span>
                        )}
                    </div>
                    
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">
                        {message.content}
                    </div>

                    {/* Attachments with Preview */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {message.attachments.map((attachment, index) => (
                                <AttachmentPreview key={index} attachment={attachment} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Attachment Chip Component
function AttachmentChip({ file, onRemove }) {
    const isImage = file.content_type?.startsWith('image/') || file.file_type === 'image';
    
    return (
        <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors">
            {isImage ? (
                <ImageIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
            ) : (
                <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            )}
            <span className="truncate max-w-[150px] text-gray-700 font-medium">{file.filename}</span>
            <button
                type="button"
                onClick={onRemove}
                className="ml-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-0.5 transition-colors"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
}

// Attachment Preview Component (with inline preview)
function AttachmentPreview({ attachment }) {
    const isImage = attachment.file_type?.startsWith('image/');
    const isPDF = attachment.file_type === 'application/pdf';
    const isText = attachment.file_type?.startsWith('text/') || attachment.filename?.endsWith('.txt');

    if (isImage) {
        return (
            <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
                <img 
                    src={attachment.file_url} 
                    alt={attachment.filename}
                    className="max-w-full h-auto max-h-64 rounded-lg object-contain cursor-pointer hover:opacity-90 border"
                />
            </a>
        );
    }

    if (isPDF) {
        return (
            <div className="border rounded-lg overflow-hidden bg-gray-50">
                <div className="flex items-center justify-between p-3 bg-white border-b">
                    <div className="flex items-center gap-2">
                        <FileType className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-medium">{attachment.filename}</span>
                    </div>
                    <a 
                        href={attachment.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                    >
                        <Download className="w-4 h-4" />
                        Download
                    </a>
                </div>
                <iframe 
                    src={attachment.file_url}
                    className="w-full h-96 border-0"
                    title={attachment.filename}
                />
            </div>
        );
    }

    if (isText) {
        return (
            <div className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between p-2 bg-gray-50 border-b">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium">{attachment.filename}</span>
                    </div>
                    <a 
                        href={attachment.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                        <Download className="w-3 h-3" />
                    </a>
                </div>
                <iframe 
                    src={attachment.file_url}
                    className="w-full h-48 border-0 bg-white p-2 text-xs"
                    title={attachment.filename}
                />
            </div>
        );
    }

    // Generic file download
    return (
        <a 
            href={attachment.file_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border transition-colors"
        >
            <FileText className="w-5 h-5 text-gray-500" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{attachment.filename}</p>
                {attachment.file_size && (
                    <p className="text-xs text-gray-500">
                        {(attachment.file_size / 1024).toFixed(1)} KB
                    </p>
                )}
            </div>
            <Download className="w-4 h-4 text-gray-400" />
        </a>
    );
}
